import { yupResolver } from '@hookform/resolvers/yup'
import { map } from 'lodash-es'
import { FC, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Currency } from './Currency'
import Loader from '../../../common/components/Loader'
import { Coin, CoinCreate } from '../../../common/models'
import { IGroup } from '../../../common/models/groups'
import {
  createCoin,
  getCoin,
  updateCoin,
} from '../../../core/services/currencies'
import { getGroups } from '../../../core/services/groups'
import { MessageBox } from '../messages/MessageBox'
import { Button } from '@/components/ui/button'
import { Icon } from '@/common/components/Icon'

export interface ICurrencyValues {
  currencies: {
    id?: string
    name: string
    iconLink: string | null
    festivalGroupId: string
    exchangeRate: number
  }[]
}

export const CurrenciesTab: FC = ({}): JSX.Element => {
  const [groups, setGroups] = useState<IGroup[]>([])
  const [coins, setCoins] = useState<Coin[]>([])
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const response = await getGroups()
        const resp = await getCoin()
        if (resp.status === 200 && response.status === 200) {
          setValue('currencies', resp.data)
          setCoins(resp.data)
          setGroups(response.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const schema = yup.object().shape({
    currencies: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().optional(),
          name: yup.string().required('Name is required'),
          iconLink: yup.string().required('Icon is required'),
          festivalGroupId: yup.string().required('Group is required'),
          exchangeRate: yup
            .number()
            .required('Main Currency Conversion Rate is required'),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ICurrencyValues>({
    mode: 'onChange',
    resolver: yupResolver<ICurrencyValues>(schema),
  })

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'currencies',
  })

  const submit = async (values: ICurrencyValues, index: number) => {
    try {
      const id = values?.currencies?.[index]?.id
      if (id === undefined || values.currencies[index].id === '') {
        const data: CoinCreate = {
          name: values.currencies[index].name,
          festivalGroupId: values.currencies[index].festivalGroupId,
          iconKey: values.currencies[index].iconLink,
          exchangeRate: values.currencies[index].exchangeRate,
        }
        const resp = await createCoin(data)
        if (resp.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      } else {
        const data: CoinCreate = {
          name: values.currencies[index].name,
          festivalGroupId: values.currencies[index].festivalGroupId,
          iconKey:
            values.currencies[index].iconLink !== coins?.[index]?.iconLink
              ? values.currencies[index].iconLink
              : null,
          exchangeRate: values.currencies[index].exchangeRate,
        }
        const resp = await updateCoin(id, data)
        if (resp.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  return (
    <>
      {loading && <Loader />}
      <div className="space-y-7">
        {map(coins, (coin, index) => (
          <form
            id={`currencies-${index}-form`}
            key={coin.id}
            onSubmit={handleSubmit((values) => submit(values, index))}
          >
            <Currency
              errors={errors}
              groups={groups}
              register={register}
              control={control}
              currency={coin}
              key={coin.id}
              index={index}
              remove={remove}
              watch={watch}
              setValue={setValue}
              setCoins={setCoins}
              coins={coins}
            />
          </form>
        ))}
        <div className="w-full flex items-center justify-center mt-4">
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              append({
                id: '',
                name: '',
                festivalGroupId: '',
                iconLink: '',
                exchangeRate: 0,
              })
              setCoins([
                ...coins,
                {
                  id: '',
                  name: '',
                  festivalGroupId: '',
                  iconLink: '',
                  exchangeRate: 0,
                },
              ])
            }}
          >
            Add New Custom Coin
            <Icon
              icon="plus"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
          </Button>
        </div>
      </div>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
